/**
 * Root App Component
 * Router setup and Telegram theme integration
 *
 * Routes:
 * - /wallet - User wallet (deposit/withdraw)
 * - /admin - Admin panel (dashboard, users, deposits, withdrawals, reports, bonus)
 * - /worker - Worker panel (withdrawal processing)
 * - /referee - Referee panel (balance, withdrawals, token conversion)
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useSound, useBackgroundMusic, useSoundMute, preloadSounds } from '@/hooks';

// Feature Components
import { WalletLanding } from '@/features/wallet/WalletLanding';
import { DepositPage } from '@/features/wallet/DepositPage';
import { DepositPendingPage } from '@/features/wallet/DepositPendingPage';
import { WithdrawalPage } from '@/features/wallet/WithdrawalPage';
import { DashboardSection } from '@/features/admin/Dashboard/DashboardSection';
import { UsersSection } from '@/features/admin/Users/UsersSection';
import { DepositsSection } from '@/features/admin/Deposits/DepositsSection';
import { WithdrawalsSection } from '@/features/admin/Withdrawals/WithdrawalsSection';
import { ReportsSection } from '@/features/admin/Reports/ReportsSection';
import { BonusSection } from '@/features/admin/Bonus/BonusSection';
import { PaymentsSection } from '@/features/admin/Payments/PaymentsSection';
import { RefereesSection } from '@/features/admin/Referees/RefereesSection';
import { WorkerSection } from '@/features/worker/WorkerSection';
import { RefereeSection } from '@/features/referee';
import { SuperAdminSection } from '@/features/superadmin/SuperAdminSection';

// Hooks
import {
  useWallet,
  useBonus,
  useDashboard,
  useUsers,
  useUserDetail,
  useUserActions,
  useDeposits,
  useDepositDetail,
  useDepositActions,
  useWithdrawals,
  useAdminWithdrawalDetail,
  useWithdrawalActions,
  useTurnoverReport,
  useBonusConfig,
  useDepositCascade,
  useWithdrawalCascade,
  useLimits,
  useWorker,
  useReferee,
} from '@/hooks/api';

// Services
import { adminAPI, walletAPI, workerAPI, superadminAPI } from '@/services/api';

// Types
import type { ReportPeriod } from '@/features/admin/Reports/ReportsSection';
import type { WithdrawalSubmitResult } from '@/features/wallet/WithdrawalSection';
// Zod types (imported from shared contracts)
import type { AdminWithdrawalDetail, AdminDepositDetail, UserDetailResponseData } from '@club-bot/shared/contracts';

// =============================================================================
// WALLET PAGE
// =============================================================================

type WalletView = 'landing' | 'deposit' | 'withdrawal';

function WalletPage() {
  const [view, setView] = useState<WalletView>('landing');
  const [minWithdrawal, setMinWithdrawal] = useState(50); // Default, will be updated

  // Ref to capture withdrawal error immediately (React state updates are async)
  const lastWithdrawalErrorRef = useRef<string | null>(null);
  const wallet = useWallet({
    onWithdrawalError: (error) => {
      lastWithdrawalErrorRef.current = error;
    },
  });

  // Fetch bonus percentage (polls every 60s while on deposit page)
  const bonus = useBonus({
    enablePolling: view === 'deposit', // Only poll while on deposit page
  });

  // Fetch wallet status to get real minimum withdrawal
  useEffect(() => {
    walletAPI.getStatus()
      .then((response) => {
        if (response.data.success && response.data.data) {
          setMinWithdrawal(response.data.data.minimumWithdrawal);
        }
      })
      .catch((err) => {
        console.error('[WalletPage] Failed to fetch wallet status:', err);
      });
  }, []);

  // Auto-resume pending deposit - switch to deposit view
  useEffect(() => {
    if (wallet.hasPendingDeposit && view === 'landing') {
      setView('deposit');
    }
  }, [wallet.hasPendingDeposit, view]);

  const handleDeposit = useCallback(() => setView('deposit'), []);
  const handleWithdraw = useCallback(() => setView('withdrawal'), []);
  const handleBack = useCallback(() => {
    wallet.resetDeposit();
    setView('landing');
  }, [wallet]);

  // Handle withdrawal submission from WithdrawalSection
  const handleWithdrawSubmit = useCallback(async (cardNumber: string, amount: number): Promise<WithdrawalSubmitResult> => {
    console.log('[WalletPage] Withdrawal submitted:', { cardNumber, amount });

    // Reset error ref before attempt
    lastWithdrawalErrorRef.current = null;

    const result = await wallet.initiateWithdrawal(amount, cardNumber);

    if (result) {
      // Success - result contains the withdrawal data
      return { success: true };
    } else {
      // Error - use the ref which was set immediately by onWithdrawalError callback
      const errorMessage = lastWithdrawalErrorRef.current || 'Withdrawal failed';
      console.error('[WalletPage] Withdrawal failed:', { error: errorMessage });

      return {
        success: false,
        error: errorMessage,
      };
    }
  }, [wallet]);

  // Handle deposit confirmation from DepositSection
  const handleDepositConfirm = useCallback(async (amount: number, _withBonus: number) => {
    console.log('[WalletPage] Deposit confirmed:', { amount });
    await wallet.initiateDeposit(amount);
  }, [wallet]);

  // Handle check status for pending deposit
  const handleCheckStatus = useCallback(async () => {
    return wallet.checkDepositStatus();
  }, [wallet]);

  // Handle cancel deposit
  const handleCancelDeposit = useCallback(async () => {
    await wallet.cancelDeposit();
    setView('landing');
  }, [wallet]);

  // Handle receipt upload
  const handleUploadReceipt = useCallback(async (file: File) => {
    await wallet.uploadReceipt(file);
  }, [wallet]);

  // Handle deposit completion
  const handleDepositComplete = useCallback(() => {
    wallet.resetDeposit();
    setView('landing');
  }, [wallet]);

  // If deposit phase is 'waiting' or 'upload_receipt' and we have card details, show pending page
  // User stays on this page until deposit is complete (prevents accidental navigation)
  if (view === 'deposit' && (wallet.depositPhase === 'waiting' || wallet.depositPhase === 'upload_receipt') && wallet.currentDeposit?.cardNumber) {
    return (
      <DepositPendingPage
        referenceNumber={wallet.currentDeposit.referenceNumber}
        amount={wallet.currentDeposit.amount}
        cardNumber={wallet.currentDeposit.cardNumber}
        cardHolderName={wallet.currentDeposit.cardHolderName || 'CARD HOLDER'}
        bank={wallet.currentDeposit.bank ?? undefined}
        bonusPercentage={wallet.currentDeposit.bonusPercentage}
        bonusAmount={wallet.currentDeposit.bonusAmount}
        expiresAt={wallet.currentDeposit.expiresAt}
        isPaid={wallet.depositPhase === 'upload_receipt'}
        isUploadingReceipt={wallet.isUploadingReceipt}
        onCheckStatus={handleCheckStatus}
        onCancel={handleCancelDeposit}
        onUploadReceipt={handleUploadReceipt}
        onComplete={handleDepositComplete}
      />
    );
  }

  // Show loading during deposit initiation
  if (view === 'deposit' && wallet.isInitiatingDeposit) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-900 text-white">
        <div className="w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-lg">Depozit yaradılır...</p>
      </div>
    );
  }

  // Show deposit calculator (initial deposit page)
  if (view === 'deposit') {
    return (
      <DepositPage
        bonusPercentage={bonus.bonusPercentage}
        onBack={handleBack}
        onDepositConfirm={handleDepositConfirm}
      />
    );
  }

  if (view === 'withdrawal') {
    return (
      <WithdrawalPage
        minAmount={minWithdrawal}
        onSubmit={handleWithdrawSubmit}
        onBack={handleBack}
      />
    );
  }

  return (
    <WalletLanding
      onDeposit={handleDeposit}
      onWithdraw={handleWithdraw}
    />
  );
}

// =============================================================================
// ADMIN PAGE
// =============================================================================

type AdminView = 'dashboard' | 'users' | 'deposits' | 'withdrawals' | 'reports' | 'bonus' | 'cascade' | 'payments' | 'referees';

function AdminPage() {
  const [view, setView] = useState<AdminView>('dashboard');
  const [reportPeriod, setReportPeriod] = useState<ReportPeriod>('monthly');

  // Dashboard
  const dashboard = useDashboard({ fetchOnMount: view === 'dashboard' });

  // Users
  const users = useUsers({ fetchOnMount: view === 'users' });
  const userDetail = useUserDetail();
  const userActions = useUserActions({ onSuccess: () => users.refetch() });

  // Deposits
  const deposits = useDeposits({ fetchOnMount: view === 'deposits' });
  const depositDetail = useDepositDetail();
  const depositActions = useDepositActions({ onSuccess: () => deposits.refetch() });

  // Withdrawals
  const withdrawals = useWithdrawals({ fetchOnMount: view === 'withdrawals' });
  const adminWithdrawalDetail = useAdminWithdrawalDetail();
  const withdrawalActions = useWithdrawalActions({ onSuccess: () => withdrawals.refetch() });

  // Reports
  const turnoverReport = useTurnoverReport();

  // Bonus
  const bonusConfig = useBonusConfig({ fetchOnMount: view === 'bonus' });

  // Cascade & Limits (for Payments view)
  const depositCascade = useDepositCascade({ fetchOnMount: view === 'payments' });
  const withdrawalCascade = useWithdrawalCascade({ fetchOnMount: view === 'payments' });
  const limits = useLimits({ fetchOnMount: view === 'payments' });

  // Referees
  const [referees, setReferees] = useState<import('@club-bot/shared/contracts').RefereeListItem[]>([]);
  const [refereesLoading, setRefereesLoading] = useState(false);
  const fetchReferees = useCallback(async () => {
    setRefereesLoading(true);
    try {
      const response = await adminAPI.getReferees();
      if (response.data.success && response.data.data) {
        setReferees(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch referees:', err);
    } finally {
      setRefereesLoading(false);
    }
  }, []);

  // Fetch report when period changes or view switches to reports
  useEffect(() => {
    if (view === 'reports') {
      turnoverReport.fetchReport(reportPeriod);
    }
  }, [view, reportPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigation handler
  const handleNavigate = useCallback((page: string) => {
    setView(page as AdminView);
  }, []);

  // User detail handler - returns backend data directly (Zod validated)
  const handleGetUserDetails = useCallback(async (telegramId: string): Promise<UserDetailResponseData> => {
    const result = await userDetail.fetchUser(telegramId);
    if (!result) throw new Error('Failed to fetch user');
    return result;
  }, [userDetail]);

  // Deposit detail handler - now returns backend data directly (Zod validated)
  const handleGetDepositDetails = useCallback(async (referenceNumber: string): Promise<AdminDepositDetail> => {
    const result = await depositDetail.fetchDeposit(referenceNumber);
    if (!result) throw new Error('Failed to fetch deposit');
    return result;
  }, [depositDetail]);

  // Withdrawal detail handler - now returns backend data directly (Zod validated)
  const handleGetWithdrawalDetails = useCallback(async (referenceNumber: string): Promise<AdminWithdrawalDetail> => {
    const result = await adminWithdrawalDetail.fetchWithdrawal(referenceNumber);
    if (!result) throw new Error('Failed to fetch withdrawal');
    return result;
  }, [adminWithdrawalDetail]);

  // Render current view
  switch (view) {
    case 'users':
      return (
        <UsersSection
          // Pass users directly - component uses AdminUserSummary type
          users={users.users}
          isLoading={users.isLoading}
          onSearch={(q) => users.searchUsers(q)}
          onGetUserDetails={handleGetUserDetails}
          onBanUser={async (id, reason) => { await userActions.banUser(id, reason); }}
          onUnbanUser={async (id) => { await userActions.unbanUser(id); }}
          onRecreateUser={async (id, reason) => { await userActions.recreateUser(id, reason); }}
          onNavigate={handleNavigate}
        />
      );

    case 'deposits':
      return (
        <DepositsSection
          // Pass deposits directly - component uses AdminDepositSummary type
          deposits={deposits.deposits}
          isLoading={deposits.isLoading}
          onSearch={(q) => deposits.searchDeposits(q)}
          onGetDepositDetails={handleGetDepositDetails}
          // Now uses referenceNumber (string) directly
          onForceComplete={async (referenceNumber, reason) => {
            await depositActions.forceComplete(referenceNumber, reason);
          }}
          // Edit financials (USDT amount, exchange rate, commission)
          onEditFinancials={async (referenceNumber, data) => {
            await adminAPI.editDeposit(referenceNumber, data);
          }}
          onNavigate={handleNavigate}
        />
      );

    case 'withdrawals':
      return (
        <WithdrawalsSection
          // Pass withdrawals directly - component uses AdminWithdrawalSummary type
          withdrawals={withdrawals.withdrawals}
          isLoading={withdrawals.isLoading}
          onSearch={(q) => withdrawals.searchWithdrawals(q)}
          onGetWithdrawalDetails={handleGetWithdrawalDetails}
          // Now uses referenceNumber (string) directly
          onSendAgain={async (referenceNumber, reason) => {
            await withdrawalActions.resendWithdrawal(referenceNumber, reason);
          }}
          // Now uses referenceNumber (string) directly
          onEditFinancials={async (referenceNumber, data) => {
            await adminAPI.editWithdrawal(referenceNumber, data);
          }}
          onNavigate={handleNavigate}
        />
      );

    case 'reports':
      return (
        <ReportsSection
          report={turnoverReport.report}
          period={reportPeriod}
          isLoading={turnoverReport.isLoading}
          onPeriodChange={setReportPeriod}
          onNavigate={handleNavigate}
        />
      );

    case 'bonus':
      return (
        <BonusSection
          currentPercentage={bonusConfig.percentage}
          refereeConversionPercent={bonusConfig.refereeConversionPercent}
          isLoading={bonusConfig.isLoading}
          onUpdateDeposit={async (pct) => { await bonusConfig.updateDeposit(pct); bonusConfig.refetch(); }}
          onUpdateReferee={async (pct) => { await bonusConfig.updateReferee(pct); bonusConfig.refetch(); }}
          onNavigate={handleNavigate}
        />
      );

    case 'payments':
      return (
        <PaymentsSection
          depositMethods={limits.depositLimits.map(m => {
            // Find cascade position for this method
            const cascadeItem = depositCascade.positions.find(p => p.hash === m.method);
            return {
              id: m.method,
              name: m.displayName,
              isActive: cascadeItem ? cascadeItem.position > 0 : false,
              minAmount: m.minAmount,
              maxAmount: m.maxAmount,
            };
          })}
          withdrawalMethods={limits.withdrawalLimits.map(m => {
            // Find cascade position for this method
            const cascadeItem = withdrawalCascade.positions.find(p => p.hash === m.method);
            return {
              id: m.method,
              name: m.displayName,
              isActive: cascadeItem ? cascadeItem.position > 0 : false,
              minAmount: m.minAmount,
              maxAmount: m.maxAmount,
            };
          })}
          depositCascade={depositCascade.positions.map((p) => ({
            id: p.hash,
            name: p.displayName,
            isActive: p.position > 0,
          }))}
          withdrawalCascade={withdrawalCascade.positions.map((p) => ({
            id: p.hash,
            name: p.displayName,
            isActive: p.position > 0,
          }))}
          onUpdateDepositMethod={async (id, updates) => {
            // Handle isActive toggle (cascade position)
            if (updates.isActive !== undefined) {
              await adminAPI.toggleDepositMethodStatus(id, updates.isActive);
              depositCascade.refetch();
            }
            // Handle min/max changes (limits)
            if (updates.minAmount !== undefined || updates.maxAmount !== undefined) {
              await limits.updateDepositLimit(id, {
                minAmount: updates.minAmount,
                maxAmount: updates.maxAmount
              });
              limits.refetch();
            }
          }}
          onUpdateWithdrawalMethod={async (id, updates) => {
            // Handle isActive toggle (cascade position)
            if (updates.isActive !== undefined) {
              await adminAPI.toggleWithdrawalMethodStatus(id, updates.isActive);
              withdrawalCascade.refetch();
            }
            // Handle min/max changes (limits)
            if (updates.minAmount !== undefined || updates.maxAmount !== undefined) {
              await limits.updateWithdrawalLimit(id, {
                minAmount: updates.minAmount,
                maxAmount: updates.maxAmount
              });
              limits.refetch();
            }
          }}
          onReorderDepositCascade={async (items) => {
            await depositCascade.reorderCascade(items.map(i => i.id));
            depositCascade.refetch();
          }}
          onReorderWithdrawalCascade={async (items) => {
            await withdrawalCascade.reorderCascade(items.map(i => i.id));
            withdrawalCascade.refetch();
          }}
          onNavigate={handleNavigate}
          isLoading={limits.isLoading || depositCascade.isLoading || withdrawalCascade.isLoading}
        />
      );

    case 'referees':
      return (
        <RefereesSection
          referees={referees}
          isLoading={refereesLoading}
          onRefresh={fetchReferees}
          onNavigate={handleNavigate}
        />
      );

    case 'dashboard':
    default:
      return (
        <DashboardSection
          stats={dashboard.dashboard}
          isLoading={dashboard.isLoading}
          onRefresh={dashboard.refetch}
          onNavigate={handleNavigate}
        />
      );
  }
}

// =============================================================================
// WORKER PAGE
// =============================================================================

function WorkerPage() {
  const worker = useWorker();

  return (
    <WorkerSection
      withdrawals={worker.withdrawals.map(w => ({
        id: w.referenceNumber,
        referenceNumber: w.referenceNumber,
        telegramId: w.telegramId,
        username: w.username || null,
        firstName: w.firstName || null,
        amount: w.amount,
        actualPayout: w.actualPayout ?? null,
        bonusDeducted: w.bonusDeducted ?? null,
        editedAmount: w.editedAmount ?? null,
        cardNumber: w.cardNumber || null,
        cardHolderName: null,
        cardBank: null,
        status: w.status,
        providerTransactionId: w.providerTransactionId || null,
        createdAt: w.createdAt,
        isLocked: w.isLocked,
        lockedBy: w.lockedBy || null,
      }))}
      isLoading={worker.isLoading}
      currentUserId={worker.currentUserId}
      onRefresh={worker.refetch}
      onLock={async (id) => { await worker.lock(id); }}
      onAutoCascade={async (id) => {
        const result = await worker.autoCascade(id);
        // autoCascade returns the API response or null on error
        if (result) {
          return { success: true, status: result.status || 'pending' };
        }
        return { success: false, error: 'Cascade failed' };
      }}
      onCheckStatus={async (id) => {
        console.log('[WorkerPage] Check status called for', id);
        try {
          const response = await workerAPI.getWithdrawalDetails(id);
          if (response.data.success && response.data.data?.transaction) {
            const status = response.data.data.transaction.status;
            // Map backend status to expected values
            // 'paid' or 'completed' means webhook confirmed payment
            if (status === 'paid' || status === 'completed') {
              return { success: true, status: 'completed' };
            }
            return { success: true, status: status || 'pending' };
          }
          return { success: false, error: 'Failed to fetch status' };
        } catch (error) {
          console.error('[WorkerPage] Check status error:', error);
          return { success: false, error: 'Failed to check status' };
        }
      }}
      onManualComplete={async (id, photo) => {
        await worker.uploadAndComplete(id, photo);
      }}
      onReject={async (id, reason) => { await worker.decline(id, reason); }}
      onEditAmount={async (id, newAmount, _reason) => { await worker.editAmount(id, newAmount); }}
      onConfirmComplete={async (id) => { await worker.confirmComplete(id); }}
      isOwnLock={worker.isOwnLock}
    />
  );
}

// =============================================================================
// REFEREE PAGE
// =============================================================================

function RefereePage() {
  const referee = useReferee();

  return (
    <RefereeSection
      profile={referee.profile}
      balanceLogs={referee.balanceLogs}
      conversions={referee.conversions}
      withdrawals={referee.withdrawals}
      isLoading={referee.isLoadingProfile}
      isLoadingProfile={referee.isLoadingProfile}
      isLoadingLogs={referee.isLoadingLogs}
      isLoadingConversions={referee.isLoadingConversions}
      isLoadingWithdrawals={referee.isLoadingWithdrawals}
      isConverting={referee.isConverting}
      isCreatingWithdrawal={referee.isCreatingWithdrawal}
      conversionBonusPercent={10} // TODO: Get from backend bonus_settings
      onRefreshProfile={referee.refetchProfile}
      onFetchLogs={referee.fetchLogs}
      onFetchMoreLogs={referee.fetchMoreLogs}
      hasMoreLogs={referee.hasMoreLogs}
      onFetchConversions={referee.fetchConversions}
      onFetchWithdrawals={referee.fetchWithdrawals}
      onSendTokens={async (targetId, amount) => {
        await referee.convertToTokens(amount, targetId);
      }}
      onSendWithdrawal={async (amount, cardNumber) => {
        await referee.createWithdrawal(amount, cardNumber);
      }}
      error={referee.profileError || referee.conversionError || referee.createWithdrawalError}
    />
  );
}

// =============================================================================
// SUPER ADMIN PAGE
// =============================================================================

function SuperAdminPage() {
  const [staff, setStaff] = useState<Array<{
    identifier: string;
    role: 'super_admin' | 'admin' | 'worker' | 'referee';
    active: boolean;
    createdAt: string;
    lastActiveDate: string | null;
    name?: string | null;
    referralCode?: string | null;
    balance?: number | null;
  }>>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchStaff = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await superadminAPI.listStaff();
      if (response.data.success && response.data.data) {
        setStaff(response.data.data.staff);
      }
    } catch (error) {
      console.error('[SuperAdmin] Failed to fetch staff:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const handleCreateStaff = useCallback(async (
    telegramId: string,
    role: 'admin' | 'worker' | 'referee',
    name?: string,
    referralCode?: string
  ) => {
    try {
      const response = await superadminAPI.createStaff({ telegramId, role, name, referralCode });
      if (response.data.success) {
        return { success: true };
      }
      return { success: false, error: 'Failed to create staff' };
    } catch (error) {
      const err = error as { response?: { data?: { error?: string } } };
      return { success: false, error: err.response?.data?.error || 'Failed to create staff' };
    }
  }, []);

  const handleDeactivate = useCallback(async (identifier: string) => {
    try {
      const response = await superadminAPI.deactivateStaff(identifier);
      if (response.data.success) {
        return { success: true };
      }
      return { success: false, error: 'Failed to deactivate staff' };
    } catch (error) {
      const err = error as { response?: { data?: { error?: string } } };
      return { success: false, error: err.response?.data?.error || 'Failed to deactivate' };
    }
  }, []);

  const handleReactivate = useCallback(async (identifier: string) => {
    try {
      const response = await superadminAPI.reactivateStaff(identifier);
      if (response.data.success) {
        return { success: true };
      }
      return { success: false, error: 'Failed to reactivate staff' };
    } catch (error) {
      const err = error as { response?: { data?: { error?: string } } };
      return { success: false, error: err.response?.data?.error || 'Failed to reactivate' };
    }
  }, []);

  return (
    <SuperAdminSection
      staff={staff}
      isLoading={isLoading}
      onRefresh={fetchStaff}
      onCreateStaff={handleCreateStaff}
      onDeactivateStaff={handleDeactivate}
      onReactivateStaff={handleReactivate}
    />
  );
}

// =============================================================================
// SOUND TOGGLE BUTTON
// =============================================================================

function SoundToggle() {
  const { isMuted, toggleMute } = useSoundMute();

  return (
    <button
      onClick={toggleMute}
      data-no-sound
      className="fixed top-4 right-4 z-50 p-2 opacity-70 hover:opacity-100 transition-opacity"
      aria-label={isMuted ? 'Unmute sounds' : 'Mute sounds'}
    >
      {isMuted ? (
        // Speaker with X (muted)
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M11 5L6 9H2v6h4l5 4V5z"
            fill="white"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <line x1="23" y1="9" x2="17" y2="15" stroke="white" strokeWidth="2" strokeLinecap="round" />
          <line x1="17" y1="9" x2="23" y2="15" stroke="white" strokeWidth="2" strokeLinecap="round" />
        </svg>
      ) : (
        // Speaker with waves (unmuted)
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M11 5L6 9H2v6h4l5 4V5z"
            fill="white"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M15.54 8.46a5 5 0 0 1 0 7.07"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M19.07 4.93a10 10 0 0 1 0 14.14"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      )}
    </button>
  );
}

// =============================================================================
// LAYOUT WRAPPER (for player/wallet pages only - includes sound)
// =============================================================================

function PlayerLayout({ children }: { children: React.ReactNode }) {
  const { play: playSound } = useSound();
  const bgMusic = useBackgroundMusic();
  const [musicStarted, setMusicStarted] = useState(false);

  useEffect(() => {
    // Preload all sounds on mount
    preloadSounds();
  }, []);

  // Click sound for buttons + start background music on first interaction
  useEffect(() => {
    const handleGlobalClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Start background music on first interaction (browsers block autoplay)
      if (!musicStarted) {
        bgMusic.play();
        setMusicStarted(true);
      }

      // Check if clicked element is a button or has role="button"
      const isButton =
        target.tagName === 'BUTTON' ||
        target.closest('button') ||
        target.getAttribute('role') === 'button' ||
        target.closest('[role="button"]');

      // Skip if element has data-no-sound attribute
      const hasNoSound =
        target.hasAttribute('data-no-sound') ||
        target.closest('[data-no-sound]');

      if (isButton && !hasNoSound) {
        playSound('click');
      }
    };

    document.addEventListener('click', handleGlobalClick, true);
    return () => document.removeEventListener('click', handleGlobalClick, true);
  }, [playSound, bgMusic, musicStarted]);

  return (
    <div className="min-h-screen bg-slate-100">
      <SoundToggle />
      {children}
    </div>
  );
}

// Staff layout wrapper (no sound toggle for admin/worker/referee)
function StaffAppLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-100">
      {children}
    </div>
  );
}

// =============================================================================
// APP
// =============================================================================

function App() {
  useEffect(() => {
    // Initialize Telegram WebApp
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.ready();
      tg.expand();
    }
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        {/* Main Bot - Wallet (with sound/music) */}
        <Route path="/wallet" element={<PlayerLayout><WalletPage /></PlayerLayout>} />

        {/* Admin Bot - Admin Panel (no sound) */}
        <Route path="/admin" element={<StaffAppLayout><AdminPage /></StaffAppLayout>} />

        {/* Withdrawal Bot - Worker Panel (no sound) */}
        <Route path="/worker" element={<StaffAppLayout><WorkerPage /></StaffAppLayout>} />

        {/* Referee Bot - Referee Panel (no sound) */}
        <Route path="/referee" element={<StaffAppLayout><RefereePage /></StaffAppLayout>} />

        {/* Super Admin - Staff Management (no sound) */}
        <Route path="/superadmin" element={<StaffAppLayout><SuperAdminPage /></StaffAppLayout>} />

        {/* Default redirect to wallet */}
        <Route path="/" element={<Navigate to="/wallet" replace />} />
        <Route path="*" element={<Navigate to="/wallet" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
